import { useState } from "react";
import { Check, X, HelpCircle, ClipboardList, Loader2, Wand2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// Mirrors backend/audit/scoring.py's gbp-pillar manual sub-signals 1:1 — these
// are the ~20 GBP-completeness checks invisible via the public Places API
// (services, description, photo categories, posts, review responses). Keep
// the codes and grouping here in sync with scoring.py's `_manual(...)` calls.
//
// `analyzeKind` on a group enables the paste-and-analyze textarea (backend:
// audit/gbp_text_analysis.py) — most of that group's items get derived from
// the pasted text instead of judged one by one. `aiKey` on an item enables
// the Vision-AI suggestion hint (backend: vision_service.classify_photo_labels).
const GROUPS = [
  {
    label: "Profil temel bilgiler",
    items: [
      { code: "verified", label: "Doğrulanmış" },
      { code: "special_hours", label: "Özel çalışma saatleri girilmiş" },
    ],
  },
  {
    label: "Kategori",
    items: [
      { code: "primary_category_correct", label: "Ana kategori seçilmiş ve işletmeye uygun" },
      { code: "category_specific", label: "Kategori yeterince spesifik" },
      { code: "no_irrelevant_categories", label: "Gereksiz/alakasız kategori yok" },
    ],
  },
  {
    label: "Hizmetler",
    analyzeKind: "services",
    analyzePlaceholder: "GBP'deki Hizmetler sekmesinden kopyalayıp buraya yapıştırın (her satıra bir hizmet)",
    items: [
      { code: "services_added", label: "Hizmetler eklenmiş" },
      { code: "services_complete", label: "Önemli hizmetler eksiksiz" },
      { code: "service_descriptions", label: "Hizmet açıklamaları var" },
      { code: "services_match_website", label: "Hizmetler web sitesiyle uyumlu" },
    ],
  },
  {
    label: "İşletme açıklaması",
    analyzeKind: "description",
    analyzePlaceholder: "GBP'deki İşletme açıklaması metnini buraya yapıştırın",
    items: [
      { code: "description_present", label: "Açıklama mevcut" },
      { code: "description_has_services", label: "Ana hizmetler geçiyor" },
      { code: "description_has_location", label: "Lokasyon geçiyor" },
      { code: "description_has_usp", label: "Farklılaştırıcı özellik (USP) geçiyor — elle karar verin" },
      { code: "no_keyword_stuffing", label: "Anahtar kelime yığılması yok" },
    ],
  },
  {
    label: "Görseller",
    aiAction: "photos",
    items: [
      { code: "photo_logo", label: "Logo", aiKey: "logo" },
      { code: "photo_cover", label: "Kapak fotoğrafı" },
      { code: "photo_exterior", label: "Dış cephe", aiKey: "exterior" },
      { code: "photo_interior", label: "İç mekân", aiKey: "interior" },
      { code: "photo_product", label: "Ürün fotoğrafları" },
      { code: "photo_service", label: "Hizmet/proje fotoğrafları" },
    ],
  },
  {
    label: "Engagement / İçerik",
    items: [
      { code: "posts_active", label: "Google Postlar güncel" },
      { code: "products_listed", label: "Ürünler bölümü dolu" },
      { code: "qanda_present", label: "Soru-Cevap (Q&A) bölümü var" },
      { code: "updates_recent", label: "Güncel bilgiler var" },
    ],
  },
  {
    label: "Yorumlar (Reviews pillar)",
    items: [
      { code: "owner_responds", label: "İşletme yorumlara cevap veriyor" },
    ],
  },
];

const TriStateButton = ({ value, target, onClick, icon: Icon, activeCls, title }) => (
  <button
    type="button"
    title={title}
    onClick={() => onClick(target)}
    className={`flex h-6 w-6 items-center justify-center rounded border text-xs transition-colors ${
      value === target ? activeCls : "border-input text-muted-foreground/50 hover:bg-accent"
    }`}
  >
    <Icon className="h-3.5 w-3.5" />
  </button>
);

// Textarea + "Analiz Et": pastes real GBP text, backend derives most of the
// group's checks from it (see analyzeKind above) instead of the admin
// judging each one by eye.
const PasteAnalyzer = ({ kind, placeholder, onAnalyze }) => {
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);

  const run = async () => {
    if (!text.trim()) return;
    setRunning(true);
    try {
      const { applied } = await onAnalyze(kind, text);
      const n = Object.keys(applied || {}).length;
      toast.success(n ? `${n} madde metinden tespit edildi` : "Metinden bir şey tespit edilemedi");
    } catch (e) {
      toast.error("Analiz edilemedi");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mb-2 flex gap-2">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className="text-xs"
      />
      <Button
        variant="outline" size="sm" className="h-auto shrink-0 text-xs"
        onClick={run} disabled={running || !text.trim()}
      >
        {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
        Analiz Et
      </Button>
    </div>
  );
};

export const ManualGbpChecklist = ({ business, onUpdate, onAnalyzeText, onSuggestPhotos }) => {
  const [saving, setSaving] = useState(null); // code currently in flight
  const [suggesting, setSuggesting] = useState(false);
  if (!business || !onUpdate) return null;

  const mgr = business.manual_gbp_review || {};
  const aiSuggestions = business.photo_ai_suggestions || {};

  const setCheck = async (code, value) => {
    // Clicking the already-active state clears it back to "not yet reviewed".
    const next = mgr[code] === value ? null : value;
    setSaving(code);
    try {
      await onUpdate(business, { [code]: next });
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(null);
    }
  };

  const runSuggestPhotos = async () => {
    setSuggesting(true);
    try {
      await onSuggestPhotos(business);
      toast.success("Fotoğraf önerileri güncellendi");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Fotoğraf önerisi alınamadı"
      );
    } finally {
      setSuggesting(false);
    }
  };

  const total = GROUPS.reduce((n, g) => n + g.items.length, 0);
  const reviewed = GROUPS.reduce(
    (n, g) => n + g.items.filter((it) => mgr[it.code] !== undefined && mgr[it.code] !== null).length,
    0
  );

  return (
    <div className="mt-3 rounded-lg border p-3">
      <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
        <ClipboardList className="h-4 w-4" />
        Manuel GBP Denetimi
        <span className="ml-auto text-xs font-normal text-muted-foreground">
          {reviewed}/{total} incelendi
        </span>
      </h4>
      <p className="mb-3 text-[11px] text-muted-foreground">
        Google İşletme Profilini/Maps sayfasını açıp gözünüzle kontrol ederek
        işaretleyin — bu veriler public API'den görünmüyor. İşaretlenmeyen
        maddeler puana dahil edilmez (0 olarak sayılmaz, sadece bekler).
      </p>
      <div className="space-y-3">
        {GROUPS.map((g) => (
          <div key={g.label}>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[11px] font-semibold text-muted-foreground">{g.label}</span>
              {g.aiAction === "photos" && onSuggestPhotos && (
                <Button
                  variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-[11px] text-primary"
                  onClick={runSuggestPhotos} disabled={suggesting}
                  title="Owner tarafından yüklenen fotoğrafları Vision AI ile analiz et (logo/dış/iç mekân)"
                >
                  {suggesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  AI ile öner
                </Button>
              )}
            </div>
            {g.analyzeKind && onAnalyzeText && (
              <PasteAnalyzer
                kind={g.analyzeKind}
                placeholder={g.analyzePlaceholder}
                onAnalyze={(kind, text) => onAnalyzeText(business, kind, text)}
              />
            )}
            <div className="space-y-1">
              {g.items.map((it) => {
                const suggested = it.aiKey && aiSuggestions[it.aiKey];
                const unconfirmed = mgr[it.code] === undefined || mgr[it.code] === null;
                return (
                  <div key={it.code} className="flex items-center gap-2">
                    <span className="flex-1 text-xs">
                      {it.label}
                      {suggested && unconfirmed && (
                        <span className="ml-1.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                          AI: var — onaylayın
                        </span>
                      )}
                    </span>
                    {saving === it.code ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    ) : (
                      <div className="flex items-center gap-1">
                        {unconfirmed && !suggested ? (
                          <HelpCircle
                            className="h-3.5 w-3.5 text-muted-foreground/40"
                            aria-label="Henüz incelenmedi"
                          />
                        ) : null}
                        <TriStateButton
                          value={mgr[it.code]} target={true} onClick={(v) => setCheck(it.code, v)}
                          icon={Check} activeCls="border-emerald-300 bg-emerald-50 text-emerald-700"
                          title="Var / doğru (tekrar tıkla: geri al)"
                        />
                        <TriStateButton
                          value={mgr[it.code]} target={false} onClick={(v) => setCheck(it.code, v)}
                          icon={X} activeCls="border-red-300 bg-red-50 text-red-600"
                          title="Yok / yanlış (tekrar tıkla: geri al)"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
